import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch, useSelector } from "react-redux";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { setVisibleColumns, setSortBy, setSortOrder } from "../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils.js";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar.jsx";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter.jsx";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState.jsx";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader.js";
import AddUpdateAllergenModal from "./add&updateAllergenModal.jsx";

const ManageAllergens = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const pageKey = "allergens";
  const defaultVisibleColumns = useMemo(
    () => ({
      name: true,
      description: true,
      solution: true,
    }),
    []
  );
  const defaultSortOrder = { value: "ASC", label: "Asc" };
  const defaultSortBy = { value: "Allergen_SortOrder", label: "Sort Order" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [allergens, setAllergens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [, setSelectAll] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [allergenToEdit, setAllergenToEdit] = useState(null);

  // Drag and drop refs
  const dragItem = useRef();
  const dragOverItem = useRef();

  const FetchAllergens = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ALLERGENS, {
        params: {
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
        },
      });

      if (response.data.status) {
        const allergenData = response.data.data;

        if (Array.isArray(allergenData)) {
          setAllergens(allergenData);
          setTotalRecords(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 1);
        } else {
          console.error("Unexpected API response format:", response.data);
          setAllergens([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.Message || "Failed to fetch allergens");
        setAllergens([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch allergens");
      }
    }
  }, [search, sortBy, sortOrder, pageSize, pageNumber, dispatch, navigate]);

  useEffect(() => {
    FetchAllergens();
  }, [FetchAllergens]);

  // Initialize preferences on mount
  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handlePageSizeChange = (selectedOption) => {
    setPageSize(selectedOption);
    setPageNumber(1);
  };

  const handleSort = (field) => {
    if (sortBy.value === field) {
      dispatch(
        setSortOrder({
          pageKey,
          sortOrder:
            sortOrder.value === "ASC"
              ? { value: "DESC", label: "Desc" }
              : { value: "ASC", label: "Asc" },
        })
      );
    } else {
      dispatch(setSortBy({ pageKey, sortBy: { value: field, label: field } }));
      dispatch(setSortOrder({ pageKey, sortOrder: { value: "ASC", label: "Asc" } }));
    }
    setPageNumber(1);
  };

  const clearFilters = () => {
    setSearch("");
    setPageNumber(1);
  };

  const hasFiltersApplied = () => search.trim() !== "";

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedAllergens(allergens.map((allergen) => allergen.id));
    } else {
      setSelectedAllergens([]);
    }
  };

  const handleSelectAllergen = (allergenId, e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedAllergens([...selectedAllergens, allergenId]);
    } else {
      setSelectedAllergens(selectedAllergens.filter((id) => id !== allergenId));
    }
  };

  const handleDeleteAllergen = async (allergenId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this allergen!",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_ALLERGEN, {
          params: {
            ID: allergenId,
          },
        });

        if (response.data.status) {
          showSuccessToast("Allergen deleted successfully");
          FetchAllergens();
        } else {
          showErrorToast(response.data.Message || "Failed to delete allergen");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete allergen");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAllergens.length === 0) {
      showErrorToast("Please select allergens to delete");
      return;
    }

    try {
      const result = await swal({
        title: "Are you sure?",
        text: `This will delete ${selectedAllergens.length} allergen(s). Once deleted, you will not be able to recover them!`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const allergenId of selectedAllergens) {
          try {
            const response = await axiosInstance.delete(APIs.DELETE_ALLERGEN, {
              params: {
                ID: allergenId,
              },
            });

            if (response.data.status) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (successCount > 0) {
          showSuccessToast(`${successCount} allergen(s) deleted successfully`);
        }
        if (failCount > 0) {
          showErrorToast(`Failed to delete ${failCount} allergen(s)`);
        }

        setSelectedAllergens([]);
        setSelectAll(false);
        FetchAllergens();
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to delete allergens");
    }
  };

  const handleEditAllergen = (allergen) => {
    setAllergenToEdit(allergen);
    setShowEditModal(true);
  };

  const handleAllergenSubmit = async (allergenData, isEdit) => {
    try {
      setLoading(true);
      let response;

      const solution = allergenData.solution?.trim() || "";
      const payload = {
        Allergen_Name: allergenData.name.trim(),
        Allergen_Description: allergenData.description.trim(),
        Allergen_Solution: solution,
        HasCustomerChoice: solution.length > 0,
        Allergen_SortOrder: allergenData.sortOrder || 0,
        CompID: 0,
      };

      if (isEdit) {
        response = await axiosInstance.put(APIs.PUT_UPDATE_ALLERGEN, {
          ID: allergenData.id,
          ...payload,
        });
      } else {
        response = await axiosInstance.post(APIs.POST_INSERT_ALLERGEN, payload);
      }

      if (response.data.status) {
        showSuccessToast(
          isEdit ? "Allergen updated successfully" : "Allergen added successfully"
        );
        setShowAddModal(false);
        setShowEditModal(false);
        setAllergenToEdit(null);
        FetchAllergens();
      } else {
        showErrorToast(response.data.Message || "Failed to save allergen");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to save allergen");
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.target.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
    e.target.style.backgroundColor = "#e3f2fd";
  };

  const handleDragLeave = (e) => {
    e.target.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const dragIndex = dragItem.current;
    const dragOverIndex = dragOverItem.current;

    if (dragIndex !== dragOverIndex) {
      const newAllergens = [...allergens];
      const draggedItem = newAllergens[dragIndex];
      newAllergens.splice(dragIndex, 1);
      newAllergens.splice(dragOverIndex, 0, draggedItem);

      setAllergens(newAllergens);

      try {
        const updates = newAllergens.map((allergen, index) => ({
          ID: allergen.id,
          SortOrder: index + 1,
        }));

        const response = await axiosInstance.post(APIs.POST_UPDATE_ALLERGEN_SORT_ORDER, {
          Updates: updates,
        });

        if (response.data.status) {
          showSuccessToast("Sort order updated successfully");
        } else {
          showErrorToast("Failed to update sort order");
          FetchAllergens();
        }
      } catch (err) {
        console.log(err);
        showErrorToast("Failed to update sort order");
        FetchAllergens();
      }
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.target.style.backgroundColor = "";
    e.target.style.opacity = "1";
  };

  const renderNoDataMessage = () => {
    if (loading || allergens.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No allergens found"
        description={
          hasFiltersApplied()
            ? "No allergens match your current search. Try a different search or clear it to see all allergens."
            : "There are no allergens to show right now. Add a new allergen or check back later."
        }
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Allergens</li>
          </ol>
          <h1 className="page-header mb-0">Manage Allergens</h1>
        </div>

        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Allergen
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Allergens"
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPageNumber(1);
          }}
          searchPlaceholder="Search allergens..."
          bodyFlush
          headerControls={
            <ColumnSelection
              visibleColumns={visibleColumns}
              setVisibleColumns={(cols) =>
                dispatch(setVisibleColumns({ pageKey, columns: cols }))
              }
            />
          }
        >
          <div className="px-3 pb-3">
            {selectedAllergens.length > 0 && (
              <div className="mb-3">
                <div
                  className="p-3 rounded d-flex align-items-center justify-content-between"
                  style={{
                    border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <span className="fw-bold" style={{ color: themeColor }}>
                      {selectedAllergens.length} allergen
                      {selectedAllergens.length > 1 ? "s" : ""} selected
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setSelectedAllergens([]);
                        setSelectAll(false);
                      }}
                    >
                      Clear Selection
                    </button>
                  </div>

                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
                      <i className="fa fa-trash me-1"></i>Bulk Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            <GridSectionLoader loading={loading} minHeight="280px">
              {renderNoDataMessage()}

              {allergens.length > 0 && (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{ minHeight: "200px", position: "relative", zIndex: 1 }}
                  >
                    <table
                      className="table table-hover text-nowrap"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead>
                        <tr>
                          <th className="pt-2 pb-2">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                allergens.length > 0 &&
                                selectedAllergens.length === allergens.length
                              }
                            />
                          </th>
                          <th className="pt-2 pb-2"></th>
                          <SortableColumnHeader
                            sortField="Allergen_Name"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.description && (
                            <SortableColumnHeader
                              sortField="Allergen_Description"
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleSort}
                            >
                              Description
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.solution && (
                            <SortableColumnHeader
                              sortField="Allergen_Solution"
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleSort}
                            >
                              Solution
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allergens.map((allergen, index) => (
                          <tr
                            key={allergen.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            <td className="align-middle">
                              <input
                                type="checkbox"
                                checked={selectedAllergens.includes(allergen.id)}
                                onChange={(e) => handleSelectAllergen(allergen.id, e)}
                              />
                            </td>
                            <td className="align-middle">
                              <i
                                className="fas fa-bars text-secondary"
                                style={{ cursor: "grab" }}
                              ></i>
                            </td>
                            <td className="align-middle">{allergen.allergen_Name}</td>
                            {visibleColumns.description && (
                              <td className="align-middle">
                                {allergen.allergen_Description || "-"}
                              </td>
                            )}
                            {visibleColumns.solution && (
                              <td className="align-middle">
                                {allergen.allergen_Solution || "-"}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <button
                                className="btn btn-link p-0"
                                onClick={() => handleEditAllergen(allergen)}
                                title="Edit allergen"
                              >
                                <i className="fas fa-pencil text-primary"></i>
                              </button>
                              <button
                                onClick={() => handleDeleteAllergen(allergen.id)}
                                className="btn btn-link p-0 ms-3"
                                style={{ color: "red" }}
                                title="Delete allergen"
                                disabled={loading}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      </div>

      {/* Modals */}
      <AddUpdateAllergenModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleAllergenSubmit}
        isEdit={false}
        allergen={null}
      />

      <AddUpdateAllergenModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setAllergenToEdit(null);
        }}
        onSubmit={handleAllergenSubmit}
        isEdit={true}
        allergen={allergenToEdit}
      />
    </div>
  );
};

export default ManageAllergens;
